import React, {
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  Button,
  Stepper,
  Step,
  StepLabel,
  DialogContent,
  Divider,
  DialogActions,
  useTheme
} from '@mui/material';
import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import Loader from 'src/common/Loader';
import { setErrorInfo, setSuccessInfo } from 'src/redux/modalSlice';
import { useDispatch, useSelector } from 'react-redux';
import { getAllTanks } from 'src/redux/tankSlice';
import FMSModal, { FMSModalTitle } from 'src/common/FMSModal';
import { getAllSites } from 'src/redux/siteSlice';
import {
  addNewTransaction,
  updateTransactionDetails
} from 'src/redux/transactionSlice';
import TransactionDetailsForm from './forms/TransactionDetailsForm';
import AdditionalDetailsForm from './forms/AdditionalDetailsForm';
import validationSchema from './formModel/transactionValidation';
import transactionFormModel from './formModel/transactionFormModel';
import formInitialValues from './formModel/transactionInitialValues';

const { formId, formField } = transactionFormModel;

const getNormalizedTextValue = (value) => {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number') return value;
  if (typeof value === 'object') {
    return value.name
      || value.station_name
      || value.site_name
      || value.label
      || '';
  }
  return '';
};

function renderStepContent(step, initialValues) {
  switch (step) {
    case 0:
      return <TransactionDetailsForm formField={formField} initialValues={initialValues} />;
    case 1:
      return <AdditionalDetailsForm formField={formField} />;
    default:
      return null;
  }
}

const NewTransaction = ({ open, handleClose, initialValues }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const { transactionType } = useSelector((state) => state.transactions);

  const steps = ['Transaction Details', 'Additional Details'];
  const isLastStep = activeStep === steps.length - 1;
  const currentValidationSchema = validationSchema[activeStep];
  const formInitValues = useMemo(() => {
    if (!initialValues) return formInitialValues;

    return {
      ...formInitialValues,
      ...initialValues,
      [formField.receivedStation.name]: getNormalizedTextValue(initialValues[formField.receivedStation.name]),
    };
  }, [initialValues]);

  useEffect(() => {
    if (open) {
      dispatch(getAllTanks());
      dispatch(getAllSites());
    }
    setActiveStep(0);
  }, [open]);

  const handleBack = () => {
    if (activeStep === 0) {
      handleClose();
    } else {
      setActiveStep(activeStep - 1);
    }
  };

  const submitForm = async (values, actions) => {
    setLoading(true);
    try {
      if (initialValues) {
        await dispatch(
          updateTransactionDetails({ transactionType, values })
        ).unwrap();
      } else {
        const finalValue = values.quantity - values.shortage_quantity;
        const payload = { ...values, actual_filled: finalValue };
        await dispatch(
          addNewTransaction({ transactionType, values: payload })
        ).unwrap();
      }

      dispatch(
        setSuccessInfo({
          successMsg: initialValues
            ? 'Transaction updated successfully'
            : 'Transaction created successfully'
        })
      );

      handleClose();
    } catch (error) {
      actions.setSubmitting(false);
      dispatch(setErrorInfo({ errorMsg: error.error_message }));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (values, actions) => {
    if (isLastStep) {
      submitForm(values, actions);
    } else {
      setActiveStep((prev) => prev + 1);
      actions.setTouched({});
      actions.setSubmitting(false);
    }
  };

  const getActionButtonLabel = () => {
    if (!isLastStep) return 'Next';
    return initialValues ? 'Update Transaction' : 'Create Transaction';
  };

  return (
    <FMSModal open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <FMSModalTitle
        title={initialValues ? 'Edit Transaction' : 'Add New Transaction'}
        onCancel={handleClose}
      />

      <Divider />

      <DialogContent sx={{ p: 3 }}>
        {!loading && (
          <Stepper
            activeStep={activeStep}
            sx={{
              mb: 4,
              '& .MuiStepLabel-label': {
                fontWeight: 600
              }
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        <Formik
          initialValues={formInitValues}
          enableReinitialize
          validationSchema={currentValidationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (loading ? (
            <Loader />
          ) : (
            <Form id={formId}>
              {renderStepContent(activeStep, initialValues)}

              <DialogActions
                sx={{
                  mt: 4,
                  pt: 2,
                  borderTop: `1px solid ${theme.palette.divider}`
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleBack}
                  sx={{
                    borderRadius: 1.5,
                    textTransform: 'none',
                    fontWeight: 600
                  }}
                >
                  {activeStep === 0 ? 'Cancel' : 'Back'}
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  sx={{
                    borderRadius: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3
                  }}
                >
                  {getActionButtonLabel()}
                </Button>
              </DialogActions>
            </Form>
          ))}
        </Formik>
      </DialogContent>
    </FMSModal>
  );
};

NewTransaction.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  initialValues: PropTypes.object
};

export default NewTransaction;
